import * as React from 'react'
import type { Editor } from '@tiptap/react'
import { useTranslations } from 'next-intl'
import type { VariantProps } from 'class-variance-authority'
import { Link } from 'lucide-react'
import type { toggleVariants } from '@/components/ui/toggle'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { ToolbarButton } from '../toolbar-button'
import { LinkEditBlock } from './link-edit-block'

interface LinkEditPopoverProps extends VariantProps<typeof toggleVariants> {
  editor: Editor
  modal?: boolean
}

const LinkEditPopover = ({
  editor,
  size,
  variant,
  modal,
}: LinkEditPopoverProps) => {
  const t = useTranslations('tiptap')
  const [open, setOpen] = React.useState(false)

  const { from, to } = editor.state.selection
  const text = editor.state.doc.textBetween(from, to, ' ')

  const onSetLink = React.useCallback(
    (url: string, text?: string, openInNewTab?: boolean) => {
      editor
        .chain()
        .focus()
        .extendMarkRange('link')
        .insertContent({
          type: 'text',
          text: text || url,
          marks: [
            {
              type: 'link',
              attrs: {
                href: url,
                target: openInNewTab ? '_blank' : '',
              },
            },
          ],
        })
        .setLink({ href: url })
        .run()

      editor.commands.enter()

      setOpen(false)
    },
    [editor],
  )

  return (
    <Popover open={open} onOpenChange={setOpen} modal={modal}>
      <PopoverTrigger asChild>
        <ToolbarButton
          isActive={editor.isActive('link')}
          tooltip={t('link')}
          aria-label={t('link')}
          disabled={editor.isActive('codeBlock')}
          size={size}
          variant={variant}
        >
          <Link className="size-5" />
        </ToolbarButton>
      </PopoverTrigger>
      <PopoverContent
        disablePortal
        className="w-full min-w-80"
        align="end"
        side="bottom"
      >
        <LinkEditBlock onSave={onSetLink} defaultText={text} />
      </PopoverContent>
    </Popover>
  )
}

export { LinkEditPopover }
